import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft, Check, User, Home, ShieldCheck, MessageSquare, Send, Phone } from "lucide-react";
import { companyInfo } from "../data/mock";

const services = [
  { id: "porte-blindee", label: "Porte blindée Fichet", icon: ShieldCheck },
  { id: "remplacement-serrure", label: "Remplacement de serrure", icon: ShieldCheck },
  { id: "ouverture-porte", label: "Ouverture de porte", icon: ShieldCheck },
  { id: "depannage-porte", label: "Dépannage de porte", icon: ShieldCheck },
  { id: "depannage-coffre-fort", label: "Dépannage coffre-fort", icon: ShieldCheck },
  { id: "autre", label: "Autre / conseil", icon: MessageSquare },
];

const logementTypes = ["Appartement", "Maison individuelle", "Local professionnel", "Autre"];
const urgences = ["Dès que possible", "Sous 1 semaine", "Sous 1 mois", "URGENCE 24/7"];

const Devis = () => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    service: "",
    logement: "",
    urgence: "",
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    codePostal: "",
    ville: "",
    adresse: "",
    message: "",
    rgpd: false,
  });

  const update = (k, v) => setForm({ ...form, [k]: v });

  const canNext = () => {
    if (step === 1) return !!form.service;
    if (step === 2) return !!form.logement && !!form.urgence;
    if (step === 3)
      return form.nom && form.prenom && form.email && form.telephone && form.codePostal && form.rgpd;
    return true;
  };

  const submit = (e) => {
    e.preventDefault();
    // Persist locally so backend can be plugged later
    const stored = JSON.parse(localStorage.getItem("fichet_devis") || "[]");
    stored.push({ ...form, date: new Date().toISOString() });
    localStorage.setItem("fichet_devis", JSON.stringify(stored));
    setSubmitted(true);
  };

  const progressPercent = ((step - 1) / 3) * 100;

  if (submitted) {
    return (
      <div className="bg-gray-50 min-h-[70vh] py-20">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white p-10 border-t-4 border-[#C9A227] text-center">
            <div className="w-20 h-20 bg-[#C9A227] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-black" strokeWidth={3} />
            </div>
            <h1 data-testid="devis-success-title" className="text-3xl font-black tracking-tight text-black mb-4">Merci {form.prenom} !</h1>
            <p className="text-gray-700 leading-relaxed mb-6">
              Votre demande de devis a bien été enregistrée. Un conseiller Fichet Serrurerie vous contactera dans les prochaines heures au <strong>{form.telephone}</strong> ou par email sur <strong>{form.email}</strong>.
            </p>
            <div className="bg-gray-50 p-5 text-left mb-6">
              <div className="text-sm text-gray-500 mb-1">Récapitulatif</div>
              <div className="text-black font-semibold">{services.find(s => s.id === form.service)?.label}</div>
              <div className="text-sm text-gray-600 mt-1">{form.logement} — {form.urgence}</div>
              <div className="text-sm text-gray-600">{form.adresse} {form.codePostal} {form.ville}</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="inline-flex items-center justify-center gap-2 bg-[#123B2E] text-white font-bold px-6 py-3 hover:bg-[#1B4D3C] transition-colors">
                Retour à l'accueil
              </Link>
              <a href={`tel:${companyInfo.emergencyPhone.replace(/\s/g, "")}`} className="inline-flex items-center justify-center gap-2 border-2 border-black text-black font-bold px-6 py-3 hover:bg-[#123B2E] hover:text-white transition-colors">
                <Phone className="w-4 h-4" /> Urgence : {companyInfo.emergencyPhone}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-[#123B2E] text-white py-14">
        <div className="max-w-3xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-[#C9A227] transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Demande de devis</span>
          </nav>
          <div className="inline-block bg-[#C9A227] text-black text-xs font-bold tracking-widest uppercase px-3 py-1.5 mb-4">Gratuit et sans engagement</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-tight">Demandez votre devis Fichet Serrurerie</h1>
          <div className="inline-flex items-center gap-2 bg-[#C9A227] text-black text-sm font-bold px-4 py-2 mt-4">
            Dépannage express dans toute la commune
          </div>
          <p className="text-white/80 mt-3">Réponse sous 24h ouvrées — devis détaillé par un spécialiste des serrures Fichet</p>
        </div>
      </div>

      {/* Form */}
      <div className="py-14">
        <div className="max-w-3xl mx-auto px-6">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold text-black">Étape {step} sur 4</div>
              <div className="text-sm text-gray-500">{Math.round(progressPercent + 25)}%</div>
            </div>
            <div className="h-1.5 bg-gray-200 relative overflow-hidden">
              <div className="h-full bg-[#C9A227] transition-all duration-500" style={{ width: `${progressPercent + 25}%` }} />
            </div>
            <div className="flex justify-between mt-3 text-xs text-gray-500">
              <span className={step >= 1 ? "font-bold text-black" : ""}>Service</span>
              <span className={step >= 2 ? "font-bold text-black" : ""}>Logement</span>
              <span className={step >= 3 ? "font-bold text-black" : ""}>Coordonnées</span>
              <span className={step >= 4 ? "font-bold text-black" : ""}>Confirmation</span>
            </div>
          </div>

          <form onSubmit={submit} className="bg-white p-8 md:p-10 border-t-4 border-[#C9A227]">
            {/* STEP 1 */}
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-black tracking-tight text-black mb-2">Quel est votre besoin ?</h2>
                <p className="text-gray-600 mb-6">Sélectionnez le service qui vous intéresse.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => update("service", s.id)}
                      data-testid={`devis-service-${s.id}`}
                      className={`flex items-center gap-3 p-4 border-2 text-left transition-all ${
                        form.service === s.id
                          ? "border-[#C9A227] bg-[#C9A227]/10"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${form.service === s.id ? "bg-[#C9A227]" : "bg-gray-100"}`}>
                        <s.icon className="w-5 h-5 text-black" />
                      </div>
                      <span className="font-semibold text-black">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-black tracking-tight text-black mb-2">Votre logement et l'urgence</h2>
                <p className="text-gray-600 mb-6">Quelques informations pour orienter votre demande.</p>

                <div className="mb-6">
                  <label className="block text-sm font-bold text-black mb-3">Type de logement</label>
                  <div className="grid grid-cols-2 gap-3">
                    {logementTypes.map((l) => (
                      <button
                        type="button"
                        key={l}
                        onClick={() => update("logement", l)}
                        data-testid={`devis-logement-${l}`}
                        className={`p-4 border-2 font-semibold transition-all ${
                          form.logement === l ? "border-[#C9A227] bg-[#C9A227]/10" : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <Home className="w-5 h-5 mx-auto mb-1.5 text-black" />
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-black mb-3">Délai souhaité</label>
                  <div className="grid grid-cols-2 gap-3">
                    {urgences.map((u) => (
                      <button
                        type="button"
                        key={u}
                        onClick={() => update("urgence", u)}
                        data-testid={`devis-urgence-${u}`}
                        className={`p-3 border-2 font-semibold text-sm transition-all ${
                          form.urgence === u ? "border-[#C9A227] bg-[#C9A227]/10" : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <div>
                <h2 className="text-2xl font-black tracking-tight text-black mb-2">Vos coordonnées</h2>
                <p className="text-gray-600 mb-6">Un conseiller vous rappelle sous 24h ouvrées.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Prénom *</label>
                    <input type="text" data-testid="devis-input-prenom" required value={form.prenom} onChange={(e) => update("prenom", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 focus:border-[#C9A227] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom *</label>
                    <input type="text" data-testid="devis-input-nom" required value={form.nom} onChange={(e) => update("nom", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 focus:border-[#C9A227] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email *</label>
                    <input type="email" data-testid="devis-input-email" required value={form.email} onChange={(e) => update("email", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 focus:border-[#C9A227] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Téléphone *</label>
                    <input type="tel" data-testid="devis-input-telephone" required value={form.telephone} onChange={(e) => update("telephone", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 focus:border-[#C9A227] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Code postal *</label>
                    <input type="text" data-testid="devis-input-codepostal" required value={form.codePostal} onChange={(e) => update("codePostal", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 focus:border-[#C9A227] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ville</label>
                    <input type="text" data-testid="devis-input-ville" value={form.ville} onChange={(e) => update("ville", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 focus:border-[#C9A227] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Adresse (optionnel)</label>
                    <input type="text" value={form.adresse} onChange={(e) => update("adresse", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 focus:border-[#C9A227] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30" />
                  </div>
                </div>
                <label className="flex items-start gap-3 mt-6 cursor-pointer">
                  <input type="checkbox" data-testid="devis-rgpd" required checked={form.rgpd} onChange={(e) => update("rgpd", e.target.checked)}
                    className="mt-1 w-5 h-5 accent-[#C9A227]" />
                  <span className="text-sm text-gray-600">J'accepte que mes données soient utilisées pour traiter ma demande de devis, conformément à la politique de confidentialité de Fichet Serrurerie.</span>
                </label>
              </div>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <div>
                <h2 className="text-2xl font-black tracking-tight text-black mb-2">Un message pour nous ?</h2>
                <p className="text-gray-600 mb-6">Précisez votre situation pour un devis plus précis (optionnel).</p>
                <textarea rows={5} data-testid="devis-message" value={form.message} onChange={(e) => update("message", e.target.value)}
                  placeholder="Ex: Porte d'entrée d'appartement, 3ème étage, actuellement porte en bois avec serrure 3 points…"
                  className="w-full px-4 py-3 border border-gray-300 focus:border-[#C9A227] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30" />
                <div className="mt-6 bg-gray-50 p-5">
                  <div className="text-sm font-bold text-black mb-3">Récapitulatif</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div><span className="text-gray-500">Service :</span> {services.find(s => s.id === form.service)?.label}</div>
                    <div><span className="text-gray-500">Logement :</span> {form.logement}</div>
                    <div><span className="text-gray-500">Délai :</span> {form.urgence}</div>
                    <div><span className="text-gray-500">Contact :</span> {form.prenom} {form.nom} — {form.email} — {form.telephone}</div>
                    <div><span className="text-gray-500">Localisation :</span> {form.codePostal} {form.ville}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
              <button type="button" onClick={() => setStep(step - 1)} disabled={step === 1} data-testid="devis-prev-btn"
                className="inline-flex items-center gap-2 text-gray-600 font-semibold hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              {step < 4 ? (
                <button key="next" type="button" onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="devis-next-btn"
                  className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#A8861C] text-black font-bold px-6 py-3 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Continuer <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button key="submit" type="submit" data-testid="devis-submit-btn"
                  className="inline-flex items-center gap-2 bg-[#123B2E] text-white font-bold px-6 py-3 hover:bg-[#1B4D3C] transition-colors">
                  <Send className="w-4 h-4" /> Envoyer ma demande
                </button>
              )}
            </div>
          </form>

          <div className="text-center mt-8 text-sm text-gray-500">
            Vous préférez le téléphone ?{" "}
            <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`} className="font-bold text-black hover:text-[#8C6F14]">
              {companyInfo.phone}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Devis;
